import { useState, useCallback, useRef, useEffect } from 'react';
import { edgeFnBase, edgeFnHeaders } from '@/utils/googleProxy';
import { reportError } from '@/lib/reportError';

interface GeolocationResult {
  location: string;
  coords: { lat: number; lng: number } | null;
}

// Timeouts (milliseconds)
const GPS_TIMEOUT_MS = 15_000;
const REVERSE_GEOCODE_TIMEOUT_MS = 8_000;

/**
 * Hook for obtaining the user's current location as a display string + coords.
 *
 * Fixes H-102 (audit P0.4):
 * - The reverse-geocode fetch now has an AbortController-backed timeout of
 *   8s. Previously it had no timeout at all, so a hung edge function meant
 *   the loading state was never cleared and the spinner ran forever.
 * - The loading / isRequesting flags are now cleared in a single `finally`
 *   block so every code path resets them. Previously they were cleared
 *   inline in three separate places, which was fragile and had drift.
 * - The hook tracks unmount so that if the component goes away mid-request,
 *   we neither update state nor leak the in-flight fetch.
 * - The `error` state is now more descriptive so InputScreen can show the
 *   user a useful message and a retry affordance. (The InputScreen side of
 *   H-102 — destructuring `error` and rendering the retry UI — is a
 *   separate commit because it touches a much larger file.)
 */
export function useGeolocation() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRequestingRef = useRef(false);
  const mountedRef = useRef(true);
  const activeAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Abort any in-flight reverse-geocode on unmount.
      activeAbortRef.current?.abort();
      activeAbortRef.current = null;
    };
  }, []);

  const getCurrentLocation = useCallback(async (): Promise<GeolocationResult | null> => {
    if (isRequestingRef.current) {
      return null;
    }

    if (!navigator.geolocation) {
      setError('Location not supported on this device');
      return null;
    }

    isRequestingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      let position: GeolocationPosition;
      try {
        position = await new Promise<GeolocationPosition>((resolve, reject) => {
          // Outer wall-clock timeout: macOS CoreLocation can hang at the OS
          // level and the built-in `timeout` option is not always honoured,
          // so we force-reject here +1s after the native timeout deadline.
          const wallClockTimer = setTimeout(() => {
            reject(new DOMException('Geolocation wall-clock timeout', 'TimeoutError'));
          }, GPS_TIMEOUT_MS + 1_000);
          navigator.geolocation.getCurrentPosition(
            (pos) => { clearTimeout(wallClockTimer); resolve(pos); },
            (err) => { clearTimeout(wallClockTimer); reject(err); },
            {
              timeout: GPS_TIMEOUT_MS,
              enableHighAccuracy: false,
              maximumAge: 30_000,
            }
          );
        });
      } catch (gpsErr) {
        // Translate the browser error codes into user-friendly strings.
        const message =
          gpsErr instanceof GeolocationPositionError
            ? gpsErr.code === 1
              ? 'Location access denied — check your browser permissions'
              : gpsErr.code === 2
                ? 'Location unavailable — try again in a moment'
                : 'Location request timed out'
            : 'Could not get your location';
        if (mountedRef.current) setError(message);
        return null;
      }

      const { latitude, longitude } = position.coords;
      const fallbackLocation = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

      // Reverse geocode with an AbortController-backed timeout. On timeout
      // or any error, fall back to raw lat/lng so the user always gets
      // something usable and the spinner never runs forever.
      const abortCtrl = new AbortController();
      activeAbortRef.current = abortCtrl;
      const timeoutId = setTimeout(() => abortCtrl.abort(), REVERSE_GEOCODE_TIMEOUT_MS);

      try {
        const response = await fetch(`${edgeFnBase}/reverse-geocode`, {
          method: 'POST',
          headers: edgeFnHeaders,
          body: JSON.stringify({ lat: latitude, lng: longitude }),
          signal: abortCtrl.signal,
        });

        const data = await response.json();

        if (data.status === 'OK' && data.results[0]) {
          const addressComponents = data.results[0].address_components;
          const sublocalityComponent = addressComponents.find((c: { types: string[] }) =>
            c.types.includes('sublocality_level_1') || c.types.includes('sublocality')
          );
          const neighborhoodComponent = addressComponents.find((c: { types: string[] }) =>
            c.types.includes('neighborhood')
          );
          const localityComponent = addressComponents.find((c: { types: string[] }) =>
            c.types.includes('locality')
          );
          const adminComponent = addressComponents.find((c: { types: string[] }) =>
            c.types.includes('administrative_area_level_2') ||
            c.types.includes('administrative_area_level_1')
          );
          const best = sublocalityComponent || neighborhoodComponent || localityComponent || adminComponent;
          const suburb = sublocalityComponent || neighborhoodComponent;
          const city = localityComponent || adminComponent;

          let location: string;
          if (suburb && city && suburb.long_name !== city.long_name) {
            location = `${suburb.long_name}, ${city.long_name}`;
          } else {
            location = best?.long_name || data.results[0].formatted_address || fallbackLocation;
          }

          return {
            location,
            coords: { lat: latitude, lng: longitude },
          };
        }

        // Edge fn returned but with a non-OK status — fall through to coords fallback.
        return {
          location: fallbackLocation,
          coords: { lat: latitude, lng: longitude },
        };
      } catch (rgErr) {
        // AbortError is expected on unmount or timeout — swallow silently.
        // Anything else gets reported so we see it in Sentry.
        const isAbort =
          rgErr instanceof DOMException && rgErr.name === 'AbortError';
        if (!isAbort) {
          reportError('geolocation.reverseGeocode', rgErr, { latitude, longitude });
        }
        // Always return the coords fallback so the user gets something.
        return {
          location: fallbackLocation,
          coords: { lat: latitude, lng: longitude },
        };
      } finally {
        clearTimeout(timeoutId);
        if (activeAbortRef.current === abortCtrl) {
          activeAbortRef.current = null;
        }
      }
    } finally {
      // Single source of truth for resetting the loading state.
      if (mountedRef.current) setLoading(false);
      isRequestingRef.current = false;
    }
  }, []);

  return { getCurrentLocation, loading, error };
}
